import React, { useEffect, useState, useRef, useCallback } from 'react';
import Slider from "react-slick";
import { Gallery, Item } from 'react-photoswipe-gallery';
import 'photoswipe/dist/photoswipe.css';
import IconBar from './IconBar';
import { SD_VERSIONS, optionsConfig } from './constants';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { favoritesStorage, cacheStorage } from './storage';

var serverURL = window.location.protocol + '//' + (window.location.hostname.includes('localhost') ? 'pixelmuse.ai.local' : 'pixelmuse.ai');

// Clear cache on initial load
cacheStorage.clearAll();

export default function Ajax(props) {
  // State Management
  const [prompts, setPrompts] = useState([]);
  const [isSearching, setIsSearching] = useState(true);
  const [lastSearch, setLastSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFavorite, setLastFavorite] = useState(Date.now());
  
  // Refs for modern scroll detection
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const controllerRef = useRef(new AbortController());
  const lastLoadTimeRef = useRef(0);
  const pendingFetchTimeoutRef = useRef(null);

  // Data Fetching Function
  const fetchItems = useCallback(async (forceLoad = false) => {
    // Check basic conditions first
    if (isLoading || !hasMoreItems) {
      return;
    }
    
    // Handle cooldown logic
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    const loadCooldown = 1500; // 1.5 second cooldown
    
    if (!forceLoad && timeSinceLastLoad < loadCooldown) {
      // Clear any existing pending fetch
      if (pendingFetchTimeoutRef.current) {
        clearTimeout(pendingFetchTimeoutRef.current);
      }
      
      // Schedule fetch after cooldown period
      const remainingCooldown = loadCooldown - timeSinceLastLoad;
      pendingFetchTimeoutRef.current = setTimeout(() => {
        fetchItems(true); // Force load to bypass cooldown check
      }, remainingCooldown);
      
      return;
    }
    
    // Clear any pending fetch since we're executing now
    if (pendingFetchTimeoutRef.current) {
      clearTimeout(pendingFetchTimeoutRef.current);
      pendingFetchTimeoutRef.current = null;
    }
    
    setIsLoading(true);
    lastLoadTimeRef.current = now;
    
    try {
      // Build category parameter
      let cat = '';
      if (props.category === 'all') {
        cat = '';
      } else if (props.category === '') {
        cat = 'artist';
      } else {
        cat = props.category;
      }

      const selectedOptions = optionsConfig[props.sdVersionId] || [];
      if (!selectedOptions.includes(props.category)) {
        cat = selectedOptions[0];
      }

      // Build API URL
      let url = serverURL + "/api/prompts?category=" + encodeURIComponent(cat) +
        "&search=" + encodeURIComponent(props.search) +
        "&sort=" + encodeURIComponent(props.sort) +
        '&page=' + page +
        '&sd_version=' + encodeURIComponent(props.sdVersionId ? props.sdVersionId : 1) +
        (props.showFavorites ? '&favorites=' + favoritesStorage.getAll().join(',') : '');

      var response = [];

      // Check cache first
      const cachedResponse = cacheStorage.get(url);
      if (cachedResponse) {
        setIsSearching(false);
        response = cachedResponse;
      } else {
        response = await fetchPrompts(url);
        cacheStorage.set(url, response);
      }

      // Filter out prompts with missing images and log them
      const filteredResponse = response.map(term => {
        const validPrompts = term.prompts.filter(prompt => {
          if (!prompt.images || prompt.images.length === 0) {
            console.log(`Prompt ID ${prompt.id} has no images - excluding from display`);
            return false;
          }
          return true;
        });
        
        return {
          ...term,
          prompts: validPrompts
        };
      }).filter(term => term.prompts.length > 0); // Remove terms with no valid prompts

      // Process response
      if (filteredResponse.length !== 0) {
        setPrompts(prevPrompts => [...prevPrompts, ...filteredResponse]);
        setPage(prevPage => prevPage + 1);
      } else {
        setHasMoreItems(false);
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.log("Request aborted");
      } else {
        console.error("Error fetching data:", e);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMoreItems, page, props.category, props.search, props.sort, props.sdVersionId, props.showFavorites, prompts.length]);

  // Intersection Observer Setup for Modern Infinite Scroll
  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting && hasMoreItems && !isLoading) {
          // Always attempt fetch - let fetchItems handle cooldown logic
          fetchItems();
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: '400px', // Trigger 400px before sentinel comes into view
        threshold: 0.1 // Trigger when 10% of sentinel is visible
      }
    );

    // Observe the sentinel element
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      // Clear any pending delayed fetches
      if (pendingFetchTimeoutRef.current) {
        clearTimeout(pendingFetchTimeoutRef.current);
        pendingFetchTimeoutRef.current = null;
      }
    };
  }, [fetchItems, hasMoreItems, isLoading]);

  // Handle search parameter changes
  useEffect(() => {
    if (props.search !== lastSearch) {
      // Abort any pending requests
      controllerRef.current.abort();
      controllerRef.current = new AbortController();
      
      // Clear any pending delayed fetches
      if (pendingFetchTimeoutRef.current) {
        clearTimeout(pendingFetchTimeoutRef.current);
        pendingFetchTimeoutRef.current = null;
      }
      
      // Reset all state for new search
      setIsSearching(true);
      setLastSearch(props.search);
      setPage(1);
      setPrompts([]);
      setHasMoreItems(true);
      setIsLoading(false);
      lastLoadTimeRef.current = 0; // Reset cooldown for new search
    }
  }, [props.search, lastSearch]);

  // Initial load and dependency changes
  useEffect(() => {
    if (prompts.length === 0 && !isLoading && hasMoreItems) {
      fetchItems();
    }
  }, [prompts.length, isLoading, hasMoreItems, props.search, props.category, props.sort, props.sdVersionId, props.showFavorites, fetchItems]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (pendingFetchTimeoutRef.current) {
        clearTimeout(pendingFetchTimeoutRef.current);
      }
      controllerRef.current.abort();
    };
  }, []);

  // Network request function
  const fetchPrompts = async (url) => {
    const response = await fetch(url, {
      method: 'GET',
      headers: new Headers({
        Accept: 'application/json'
      }),
      signal: controllerRef.current.signal
    });

    setIsSearching(false);
    const prompts = await response.json();
    return prompts;
  };

  // Slider arrow components - Updated for Digital Renaissance theme
  function SampleNextArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          position: "absolute",
          right: "0",
          top: "50%",
          transform: "translate(-50%, -50%)"
          // Removed background override - let CSS handle styling
        }}
        onClick={onClick}
      />
    );
  }

  function SamplePrevArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        className={className}
        style={{
          ...style,
          display: "block",
          position: "absolute",
          left: "0",
          top: "50%",
          transform: "translate(50%, -50%)"
          // Removed background override - let CSS handle styling
        }}
        onClick={onClick}
      />
    );
  }

  // Mouse interaction handlers for image gallery
  const handleMouseDown = (e) => {
    e.currentTarget.dataset.startX = e.clientX;
    e.currentTarget.dataset.startY = e.clientY;
  };

  const handleMouseUp = (e, open) => {
    const startX = parseFloat(e.currentTarget.dataset.startX);
    const startY = parseFloat(e.currentTarget.dataset.startY);
    const endX = e.clientX;
    const endY = e.clientY;

    const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));

    if (distance < 5) { // Adjust the threshold as needed
      open();
    }
  };

  // UI state for details and descriptions
  const [showDetails, setShowDetails] = useState(0);
  const [showDescription, setShowDescription] = useState(0);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate SD version
  const sdVersion = SD_VERSIONS.find(v => v.id === +props.sdVersionId);
  if (!sdVersion) {
    console.error('Invalid sdVersionId:', props.sdVersionId);
    return null;
  }
  
  return (
    <>
      {/* Loading state for initial search */}
      {prompts.length === 0 && isSearching ? (
        <div 
          className="loader" 
          style={{
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '20px 0'
          }}
        >
          Loading ...
        </div>
      ) : prompts.length === 0 && !isSearching ? (
        <p style={{ textAlign: 'center' }}>
          <b>No items found</b>
        </p>
      ) : (
        <>
          {/* MAIN CONTENT - Natural document flow */}
          {prompts.map((term, index) => (
            <div key={term.term + term.id} className="form" style={{ marginBottom: '10px' }}>
              <div className="layout">
                <div>
                  <IconBar
                    termId={term.id}
                    term={term.term}
                    category={term.category}
                    serverURL={serverURL}
                    setLastFavorite={setLastFavorite}
                    setShowDetails={setShowDetails}
                    showDetails={showDetails}
                    setShowDescription={setShowDescription}
                    setDescription={setDescription}
                    setLoading={setLoading}
                    showDescription={showDescription}
                    sdVersionId={props.sdVersionId}
                  />
                  {(showDescription === term.term && (
                    <div>
                      {loading ? (
                        <p>Loading ...</p>
                      ) : (
                        <p className="description-notes">{description}</p>
                      )}
                    </div>
                  ))}
                  <div
                    className="slider-wrapper"
                    style={{
                      overflow: 'hidden',
                      marginBottom: '10px',
                    }}
                  >
                    <Gallery>
                      <Slider {...{
                        className: "slider",
                        dots: false,
                        infinite: false,
                        centerMode: false,
                        arrows: term.prompts.length > 1,
                        variableWidth: true,
                        slidesToShow: 1,
                        slidesToScroll: 1,
                        adaptiveHeight: false,
                        nextArrow: <SampleNextArrow />,
                        prevArrow: <SamplePrevArrow />
                      }}>
                        {term.prompts.map(prompt => (
                          <div key={prompt.id}>
                            <div
                              className="image-container"
                              style={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                alignItems: 'flex-start',
                              }}
                            >
                              {prompt.images.map((image) => {
                                const sdImageName = image.replace('.png', '');
                                const imageWidth = sdVersion.thumbnail.width;
                                const imageHeight = sdVersion.thumbnail.height;
                                const aspectRatio = imageHeight / imageWidth;
                                return (
                                  <div
                                    key={image}
                                    className="image-wrapper"
                                    style={{
                                      position: 'relative',
                                      width: `${imageWidth}px`,
                                      flex: '0 0 auto',
                                      marginRight: '5px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '100%',
                                        paddingTop: `${aspectRatio * 100}%`,
                                        position: 'relative',
                                      }}
                                    >
                                      <Item
                                        original={serverURL + "/stable_diffusion_images/" + sdVersion.name + "/" + sdImageName + ".jpg"}
                                        thumbnail={serverURL + "/stable_diffusion_images/" + sdVersion.name + "/" + sdImageName + ".webp"}
                                        width={sdVersion.fullSize.width}
                                        height={sdVersion.fullSize.height}
                                      >
                                        {({ ref, open }) => (
                                          <img
                                            ref={ref}
                                            onMouseDown={handleMouseDown}
                                            onMouseUp={(e) => handleMouseUp(e, open)}
                                            src={serverURL + "/stable_diffusion_images/" + sdVersion.name + "/" + sdImageName + ".webp"}
                                            alt={prompt.prompt}
                                            className="img"
                                            style={{
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover',
                                            }}
                                          />
                                        )}
                                      </Item>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {showDetails === term.term && (
                              <div className="details" key={prompt.prompt} style={{ opacity: 0.8 }}>
                                <div className="details" key={prompt.prompt} style={{ opacity: 0.8, width: sdVersion.thumbnail.width * prompt.images.length + 'px' }}>
                                  <div style={{ display: "flex" }}>
                                    <img src={serverURL + "/images/clipboard.svg"} width="12" height="12" style={{ cursor: "pointer" }} onClick={() => { navigator.clipboard.writeText(prompt.prompt) }} alt={prompt.prompt} title="copy" />
                                    <strong style={{ color: "#2986cc" }}>{prompt.prompt}</strong>
                                  </div><br />
                                  <span className="detail-item"><strong>Stable Diffusion </strong> {prompt.version} </span>
                                  <span className="detail-item"><strong>{prompt.width} x {prompt.height} </strong></span>
                                  <span className="detail-item"><strong>Seed: </strong> {prompt.seed} </span>
                                  <span className="detail-item"><strong>Scale:</strong>  {prompt.scale} </span>
                                  <span className="detail-item"><strong>Steps:</strong>  {prompt.steps} </span>
                                  <span className="detail-item"><strong>sampler:</strong>  {prompt.sampler} </span>
                                  <span className="detail-item"><strong>scheduler:</strong>  {prompt.scheduler} </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </Slider>
                    </Gallery>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* INTERSECTION OBSERVER SENTINEL - Modern infinite scroll trigger */}
          {hasMoreItems && !isLoading && (
            <div 
              ref={sentinelRef}
              className="scroll-sentinel"
              style={{
                height: '20px',
                margin: '40px 0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#999',
                fontSize: '12px'
              }}
            >
              {/* Invisible sentinel for Intersection Observer */}
            </div>
          )}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="loading-indicator">
              Loading more items...
            </div>
          )}
          
          {/* End of list indicator */}
          {!hasMoreItems && prompts.length > 0 && (
            <div className="end-indicator">
              No more items to display
            </div>
          )}
        </>
      )}
      <ToastContainer />
    </>
  );
};
